/**
 * Primitive schemas reused across every contract.
 *
 * These exist so that "a money amount" or "a date with no time" has exactly one definition. The
 * money and date types in particular are where HRMS bugs live, so the reasoning is spelled out.
 */

import { z } from 'zod';
import {
  ACCRUAL_FREQUENCIES,
  ASSESSMENT_QUESTION_TYPES,
  ASSESSMENT_STATUSES,
  ASSET_BATCH_ALLOCATION_STATUSES,
  ASSET_BATCH_STATUSES,
  ASSET_CUSTODY_SIGNOFFS,
  ASSET_DEPRECIATION_METHODS,
  ATTENDANCE_SOURCES,
  BANK_FILE_FORMATS,
  BILLING_FREQUENCIES,
  BILLING_PAYMENT_MODES,
  CALENDAR_PROVIDERS,
  CANDIDATE_SOURCES,
  CANDIDATE_STAGES,
  CANDIDATE_STATUSES,
  COUNTRIES,
  CURRENCIES,
  EMPLOYMENT_STATUSES,
  EMPLOYMENT_TYPES,
  EWA_RECOVERY_STATUSES,
  EWA_WITHDRAWAL_STATUSES,
  EXPENSE_ADVANCE_STATUSES,
  EXPENSE_CATEGORY_USAGE_TYPES,
  EXPENSE_CLAIM_STATUSES,
  EXPENSE_MILEAGE_UNITS,
  EXPENSE_PAYMENT_BATCH_STATUSES,
  EXPENSE_POLICY_RULE_PERIODS,
  EXPERIENCE_LEVELS,
  GENDERS,
  IN_STATES,
  INTEGRATION_STATUSES,
  INTERVIEW_RECOMMENDATIONS,
  INTERVIEW_STATUSES,
  INVOICE_STATUSES,
  INVOICE_TYPES,
  JOB_POSTING_STATUSES,
  LEAVE_REQUEST_STATUSES,
  LOCALES,
  MARITAL_STATUSES,
  MEETING_PROVIDERS,
  MILESTONE_STATUSES,
  PHASE_STATUSES,
  MODULE_ENTITLEMENT_SOURCES,
  MODULE_KEYS,
  OFFER_SOURCE_TYPES,
  OFFER_STATUSES,
  OFFLINE_PAYMENT_VERIFICATION_STATUSES,
  PAID_MODES,
  PAY_FREQUENCIES,
  PROJECT_BILLING_MODELS,
  PROJECT_ROLES,
  PROJECT_STATUSES,
  PAYOUT_MODES,
  PAYROLL_EXPENSE_INCLUSION_STATUSES,
  PAYROLL_LOAN_INSTALLMENT_STATUSES,
  PAYROLL_LOAN_REQUEST_STATUSES,
  PAYROLL_LOAN_STATUSES,
  PAYROLL_VARIABLE_INPUT_CATEGORIES,
  REFERRAL_BONUS_STATUSES,
  REQUISITION_STATUSES,
  SLOT_OFFER_STATUSES,
  SUBSCRIPTION_STATUSES,
  TASK_PRIORITIES,
  TIME_ENTRY_SOURCES,
  TRAVEL_BOOKING_STATUSES,
  TIMESHEET_PERIOD_TYPES,
  ATTENDANCE_CHECK_MODES,
  TIMESHEET_STATUSES,
  TRAINING_ASSIGNMENT_STATUSES,
  TRAINING_CAMERA_STATES,
  TRAINING_CONTENT_KINDS,
  TRAINING_DISPLAY_STATUSES,
  TRAINING_ISSUE_CATEGORIES,
  TRAINING_LIVE_ATTENDANCE_SOURCES,
  TRAINING_LIVE_ATTENDANCE_STATUSES,
  TRAINING_LIVE_ATTENDANCE_SYNC_STATUSES,
  TRAINING_LIVE_SESSION_AUDIENCES,
  TRAINING_LIVE_SESSION_PROVIDERS,
  TRAINING_LIVE_SESSION_STATUSES,
  TRAINING_PAUSE_REASONS,
  TRAINING_QUIZ_QUESTION_TYPES,
  TRAINING_SESSION_END_REASONS,
  TRAINING_TASK_STATUSES,
  TRAINING_VERIFICATION_STATUSES,
  WORK_FORM_FIELD_TYPES,
  WORK_FORM_KINDS,
  WORK_FORM_APPROVAL_MODES,
  WORK_FORM_MODES,
  WORK_FORM_SYSTEM_FIELDS,
  VENDOR_AMC_STATUSES,
  WORK_PLAN_ITEM_STATUSES,
  WORK_TYPES,
  ZOHO_REGIONS,
} from '../enums.js';

export const uuidSchema = z.string().uuid();
export const orgIdSchema = uuidSchema.describe('Organisation id — the RLS boundary');
export const legalEntityIdSchema = uuidSchema.describe('Legal entity id');

/**
 * Money as a decimal STRING, never a JS number.
 *
 * JSON has one numeric type and it is a float; `0.1 + 0.2` is the canonical example of why that is
 * unacceptable in payroll. Serialising as a string keeps the value exact from Postgres `numeric`
 * through the API to the client, where it is parsed with decimal.js. A `z.number()` here would
 * silently reintroduce the very defect the database column was chosen to avoid.
 */
export const decimalStringSchema = z
  .string()
  .regex(/^-?\d{1,15}(\.\d{1,4})?$/, 'Must be a decimal with at most 4 places');

export const moneySchema = z.object({
  amount: decimalStringSchema,
  currency: z.enum(CURRENCIES),
});
export type Money = z.infer<typeof moneySchema>;

/**
 * A decimal money STRING that must be strictly greater than zero — everywhere a client supplies an
 * amount being claimed, split, requested, or distanced, never a figure the server itself computes
 * (a running balance, a signed ledger total, or `signedDaysSchema`'s own kind of value legitimately
 * crosses zero). `decimalStringSchema` alone allows a leading `-` and `0`; neither is a valid thing
 * for a human to type into an expense claim, a split line, a mileage distance, or an advance request
 * — a negative or zero claim lowers period/budget totals it should never be able to touch, and a
 * negative advance disburses with a negative balance (EXP-P0-3).
 */
export const positiveDecimalStringSchema = decimalStringSchema.refine(
  (value) => Number.parseFloat(value) > 0,
  'Must be a positive amount, greater than zero',
);

/** A lower-case SHA-256 hex digest — a file checksum, or a batch/version content digest. */
export const sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/, 'Must be a lowercase sha256 hex');

/**
 * A calendar date with no time and no zone: a date of birth, a joining date, a leave day.
 *
 * Kept distinct from an instant because they behave differently. Someone born on 1990-03-15 was
 * born on that date everywhere on earth; a punch-in at a moment in time is a different local date
 * in Bangalore than in New York. Conflating the two is how "employee joined a day early" bugs
 * appear for exactly the employees in the timezones furthest from the server.
 */
export const dateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
  .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), 'Not a real date');

/** An instant. Always UTC on the wire; rendered in the viewer's or the location's zone. */
export const instantSchema = z.string().datetime({ offset: true });

/** IANA zone, e.g. "Asia/Kolkata". Every location carries one; attendance logic resolves in it. */
export const timezoneSchema = z.string().min(1).max(64);

export const emailSchema = z.string().email().max(320).toLowerCase().trim();

/** E.164. Stored normalised so a number entered three ways still matches one person. */
export const phoneSchema = z
  .string()
  .regex(/^\+[1-9]\d{7,14}$/, 'Must be E.164, e.g. +919876543210');

export const countrySchema = z.enum(COUNTRIES);
export const currencySchema = z.enum(CURRENCIES);
export const localeSchema = z.enum(LOCALES);
export const inStateSchema = z.enum(IN_STATES);
export const employmentTypeSchema = z.enum(EMPLOYMENT_TYPES);
export const employmentStatusSchema = z.enum(EMPLOYMENT_STATUSES);
/**
 * The `code` of one of the calling org's own ExitType rows (org-customizable — see ADR 0022's
 * catalogue pattern and org-setup.contract.ts). No longer a closed enum: the API layer validates
 * the value against that org's ExitType table, not against a fixed list here.
 */
export const exitTypeSchema = z.string().trim().min(1).max(40);
export const payFrequencySchema = z.enum(PAY_FREQUENCIES);
export const accrualFrequencySchema = z.enum(ACCRUAL_FREQUENCIES);
export const attendanceSourceSchema = z.enum(ATTENDANCE_SOURCES);
export const leaveRequestStatusSchema = z.enum(LEAVE_REQUEST_STATUSES);
export const expenseClaimStatusSchema = z.enum(EXPENSE_CLAIM_STATUSES);
export const expenseAdvanceStatusSchema = z.enum(EXPENSE_ADVANCE_STATUSES);
export const travelBookingStatusSchema = z.enum(TRAVEL_BOOKING_STATUSES);
export const expenseCategoryUsageTypeSchema = z.enum(EXPENSE_CATEGORY_USAGE_TYPES);
export const expensePolicyRulePeriodSchema = z.enum(EXPENSE_POLICY_RULE_PERIODS);
export const expenseMileageUnitSchema = z.enum(EXPENSE_MILEAGE_UNITS);
export const paidModeSchema = z.enum(PAID_MODES);
export const payoutModeSchema = z.enum(PAYOUT_MODES);
export const expensePaymentBatchStatusSchema = z.enum(EXPENSE_PAYMENT_BATCH_STATUSES);
export const bankFileFormatSchema = z.enum(BANK_FILE_FORMATS);
export const ewaWithdrawalStatusSchema = z.enum(EWA_WITHDRAWAL_STATUSES);
export const ewaRecoveryStatusSchema = z.enum(EWA_RECOVERY_STATUSES);
export const payrollLoanRequestStatusSchema = z.enum(PAYROLL_LOAN_REQUEST_STATUSES);
export const payrollLoanStatusSchema = z.enum(PAYROLL_LOAN_STATUSES);
export const payrollLoanInstallmentStatusSchema = z.enum(PAYROLL_LOAN_INSTALLMENT_STATUSES);
export const payrollVariableInputCategorySchema = z.enum(PAYROLL_VARIABLE_INPUT_CATEGORIES);
export const payrollExpenseInclusionStatusSchema = z.enum(PAYROLL_EXPENSE_INCLUSION_STATUSES);
export const genderSchema = z.enum(GENDERS);
export const maritalStatusSchema = z.enum(MARITAL_STATUSES);
export const workTypeSchema = z.enum(WORK_TYPES);
export const requisitionStatusSchema = z.enum(REQUISITION_STATUSES);
export const jobPostingStatusSchema = z.enum(JOB_POSTING_STATUSES);
export const candidateStageSchema = z.enum(CANDIDATE_STAGES);
export const candidateStatusSchema = z.enum(CANDIDATE_STATUSES);
export const candidateSourceSchema = z.enum(CANDIDATE_SOURCES);
export const experienceLevelSchema = z.enum(EXPERIENCE_LEVELS);
export const assetDepreciationMethodSchema = z.enum(ASSET_DEPRECIATION_METHODS);
export const assetCustodySignoffSchema = z.enum(ASSET_CUSTODY_SIGNOFFS);
export const vendorAmcStatusSchema = z.enum(VENDOR_AMC_STATUSES);
export const assetBatchStatusSchema = z.enum(ASSET_BATCH_STATUSES);
export const assetBatchAllocationStatusSchema = z.enum(ASSET_BATCH_ALLOCATION_STATUSES);
export const interviewStatusSchema = z.enum(INTERVIEW_STATUSES);
export const interviewRecommendationSchema = z.enum(INTERVIEW_RECOMMENDATIONS);
export const meetingProviderSchema = z.enum(MEETING_PROVIDERS);
export const offerStatusSchema = z.enum(OFFER_STATUSES);
export const offerSourceTypeSchema = z.enum(OFFER_SOURCE_TYPES);
export const calendarProviderSchema = z.enum(CALENDAR_PROVIDERS);
export const integrationStatusSchema = z.enum(INTEGRATION_STATUSES);
export const slotOfferStatusSchema = z.enum(SLOT_OFFER_STATUSES);
export const referralBonusStatusSchema = z.enum(REFERRAL_BONUS_STATUSES);
export const projectStatusSchema = z.enum(PROJECT_STATUSES);
export const projectBillingModelSchema = z.enum(PROJECT_BILLING_MODELS);
export const projectRoleSchema = z.enum(PROJECT_ROLES);
/**
 * ADR 0020 §6: NOT `z.enum(TASK_STATUSES)` — a task's status is any active `TaskStatusDefinition.key`
 * for the org, of which the six built-ins in `TASK_STATUSES` are only the seeded default set. The
 * wire accepts any lowercase key shape; the ROUTE HANDLER is what checks the key actually names an
 * active status for the caller's org (`assertUsableTaskStatus` in project.ts) — same division of
 * labor as `taskTypeId`, which is a bare `uuidSchema` on the wire and validated the same way.
 */
export const taskStatusSchema = z
  .string()
  .trim()
  .min(1)
  .max(50)
  .regex(/^[a-z][a-z0-9_]*$/, 'Use lowercase letters, digits and underscores, starting with a letter');
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);
export const projectMilestoneStatusSchema = z.enum(MILESTONE_STATUSES);
export const projectPhaseStatusSchema = z.enum(PHASE_STATUSES);
export const timesheetStatusSchema = z.enum(TIMESHEET_STATUSES);
export const timesheetPeriodTypeSchema = z.enum(TIMESHEET_PERIOD_TYPES);
export const timeEntrySourceSchema = z.enum(TIME_ENTRY_SOURCES);
export const attendanceCheckModeSchema = z.enum(ATTENDANCE_CHECK_MODES);
export const workFormKindSchema = z.enum(WORK_FORM_KINDS);
export const workFormModeSchema = z.enum(WORK_FORM_MODES);
export const workFormApprovalModeSchema = z.enum(WORK_FORM_APPROVAL_MODES);
export const workFormFieldTypeSchema = z.enum(WORK_FORM_FIELD_TYPES);
export const workFormSystemFieldSchema = z.enum(WORK_FORM_SYSTEM_FIELDS);
export const workPlanItemStatusSchema = z.enum(WORK_PLAN_ITEM_STATUSES);
export const assessmentQuestionTypeSchema = z.enum(ASSESSMENT_QUESTION_TYPES);
export const assessmentStatusSchema = z.enum(ASSESSMENT_STATUSES);
export const billingFrequencySchema = z.enum(BILLING_FREQUENCIES);
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export const invoiceTypeSchema = z.enum(INVOICE_TYPES);
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);
export const billingPaymentModeSchema = z.enum(BILLING_PAYMENT_MODES);
export const offlinePaymentVerificationStatusSchema = z.enum(OFFLINE_PAYMENT_VERIFICATION_STATUSES);
export const moduleKeySchema = z.enum(MODULE_KEYS);
export const moduleEntitlementSourceSchema = z.enum(MODULE_ENTITLEMENT_SOURCES);

/**
 * A leave amount — a whole/half-day count when it's a client-typed value (HR authoring
 * `annualQuota`/`carryForwardCap`, or a manual `leave.balance:adjust`), or a finer server-COMPUTED
 * value up to 2 decimal places when it's an hourly request's day-equivalent (`hours` divided by the
 * requester's shift length — see the leave contract's header). One schema for both, at the DB
 * column's own `Decimal(6,2)` precision ceiling, since the two never need different limits in
 * practice. A decimal STRING, per invariant 2, and distinct from `decimalStringSchema` because it
 * is never money — a currency-shaped regex would accept "2.333" as a leave amount, which is not.
 */
export const leaveDaysSchema = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Must be a decimal day count with at most 2 decimal places, e.g. "1", "2.5", or "0.38"')
  .refine((value) => Number.parseFloat(value) > 0, 'Must be greater than zero');

/**
 * An hourly leave request's raw input (e.g. "3" or "2.5" hours) — a per-request choice on ANY leave
 * type, converted server-side to its day-equivalent (`leaveDaysSchema`) for the actual balance
 * debit. Same decimal-STRING and half-increment convention as `leaveDaysSchema`'s client-typed
 * case, just a different unit and a sane single-day cap. See the leave contract's header.
 */
export const leaveHoursSchema = z
  .string()
  .regex(/^\d{1,2}(\.5)?$/, 'Must be a whole or half-hour count, e.g. "3" or "2.5"')
  .refine((value) => Number.parseFloat(value) > 0 && Number.parseFloat(value) <= 24, 'Must be between 0 and 24 hours');

/**
 * Which part of a day a leave request's start/end date covers. A half-day request always names
 * which half — "first_half" and "second_half" are not interchangeable with a plain half-day count,
 * because sandwich-leave and attendance-window logic need to know which half was actually worked.
 */
export const leaveSessionSchema = z.enum(['full', 'first_half', 'second_half']);

/**
 * The effective-dating pair carried by compensation, org assignment, grade, manager, and policy
 * assignment. `validTo === null` means "current". Point-in-time queries are a product requirement
 * (payroll reruns, arrears, audits), not a nice-to-have.
 */
export const effectiveDatedSchema = z.object({
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
});

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

/**
 * Cursor pagination, not offset.
 *
 * An HRMS list is read while it is being written to — someone is onboarding while HR pages through
 * the directory. Offset pagination silently skips or repeats rows when that happens. A cursor is
 * stable against concurrent inserts.
 */
export const paginationQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export function paginatedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
    total: z.number().int().nonnegative().optional(),
  });
}

// ---------------------------------------------------------------------------
// Errors on the wire
// ---------------------------------------------------------------------------

export const errorBodySchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
  requestId: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Geo
// ---------------------------------------------------------------------------

/** Attendance punches carry a fix; the geo-fence is evaluated server-side from these values. */
export const geoPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyMetres: z.number().nonnegative().optional(),
});
export type GeoPoint = z.infer<typeof geoPointSchema>;

/**
 * Idempotency key for financial mutations. A retried payroll run or payout must return the
 * original result rather than executing twice.
 */
export const idempotencyKeySchema = z.string().min(8).max(255);

// ---------------------------------------------------------------------------
// Performance — Goals & OKRs
// ---------------------------------------------------------------------------

/**
 * A percentage — a `Goal`/`KeyResult` `weightPercent` or `progressPercent` — as a decimal STRING,
 * 0-100 inclusive with at most 2 places, per invariant 2. Distinct from `decimalStringSchema`
 * because it is range-bound to a percentage, not an arbitrary money amount, and from
 * `measureValueSchema` because a percentage is never negative and never carries more than 2dp.
 */
export const percentSchema = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Must be a decimal with at most 2 places')
  .refine((value) => Number.parseFloat(value) <= 100, 'Must be at most 100');

/**
 * A `KeyResult`/`GoalKeyResult` measured value — `startValue`/`currentValue`/`targetValue` — as a
 * decimal STRING with at most 4 places, matching the `Decimal(14,4)` column. Unlike `percentSchema`
 * this is not range-bound: a `numeric` or `currency` key result's target can be any magnitude
 * (revenue, headcount, ...), and may legitimately be negative (e.g. "reduce churn by 5").
 */
export const measureValueSchema = z
  .string()
  // `Decimal(14,4)` = 14 significant digits total, 4 of them after the point — 10 before it, not 15.
  // A value with more integer digits than the column allows passed this check as recently as
  // "12345678901.5" and only failed at the database with a numeric-overflow error instead of a 422.
  .regex(
    /^-?\d{1,10}(\.\d{1,4})?$/,
    'Must be a decimal with at most 10 integer and 4 fractional digits',
  );

// ---------------------------------------------------------------------------
// Performance — Reviews
// ---------------------------------------------------------------------------

/**
 * A competency/potential rating — `Decimal(3,1)`, one decimal place, granular on a fixed scale
 * (e.g. 4.5 is expressible on a 1-5 scale — user-confirmed, see the Performance Reviews plan's Data
 * model section). Shape-only here: this schema cannot know a given `ReviewCycle.ratingScaleMax`
 * (it's per-cycle, not a fixed constant), so it only bounds the value to a generous 0-10 ceiling and
 * one decimal place — the route re-validates `>= 1` and `<= ratingScaleMax` against the review's
 * actual cycle, returning 422 with the real scale in the message when it's out of range.
 */
export const ratingValueSchema = z
  .string()
  .regex(/^\d{1,2}(\.\d)?$/, 'Must be a decimal with at most one place')
  .refine((value) => Number.parseFloat(value) <= 10, 'Must be at most 10');

/** The body of an approve/reject-style decision endpoint — an optional free-text comment, nothing
 * else. Shared so this doesn't get hand-copied per workflow-gated entity (PIP, review, calibration
 * all had their own byte-identical copy before this). */
export const commentOnlyDecisionBodySchema = z.object({
  comment: z.string().trim().max(2000).optional(),
});

// ---------------------------------------------------------------------------
// Training
// ---------------------------------------------------------------------------

export const trainingTaskStatusSchema = z.enum(TRAINING_TASK_STATUSES);
export const trainingContentKindSchema = z.enum(TRAINING_CONTENT_KINDS);
export const trainingAssignmentStatusSchema = z.enum(TRAINING_ASSIGNMENT_STATUSES);
export const trainingDisplayStatusSchema = z.enum(TRAINING_DISPLAY_STATUSES);
export const trainingCameraStateSchema = z.enum(TRAINING_CAMERA_STATES);
export const trainingSessionEndReasonSchema = z.enum(TRAINING_SESSION_END_REASONS);
export const trainingPauseReasonSchema = z.enum(TRAINING_PAUSE_REASONS);
export const trainingVerificationStatusSchema = z.enum(TRAINING_VERIFICATION_STATUSES);
export const trainingQuizQuestionTypeSchema = z.enum(TRAINING_QUIZ_QUESTION_TYPES);
export const trainingIssueCategorySchema = z.enum(TRAINING_ISSUE_CATEGORIES);
export const trainingLiveSessionProviderSchema = z.enum(TRAINING_LIVE_SESSION_PROVIDERS);
export const trainingLiveSessionAudienceSchema = z.enum(TRAINING_LIVE_SESSION_AUDIENCES);
export const trainingLiveSessionStatusSchema = z.enum(TRAINING_LIVE_SESSION_STATUSES);
export const trainingLiveAttendanceStatusSchema = z.enum(TRAINING_LIVE_ATTENDANCE_STATUSES);
export const trainingLiveAttendanceSourceSchema = z.enum(TRAINING_LIVE_ATTENDANCE_SOURCES);
export const trainingLiveAttendanceSyncStatusSchema = z.enum(TRAINING_LIVE_ATTENDANCE_SYNC_STATUSES);
export const zohoRegionSchema = z.enum(ZOHO_REGIONS);
